import * as React from "react";
import {MapLibreMapInstance} from "../MapLibreMap";
import {LngLat, Offset, PopupOptions, PositionAnchor, Point, PointLike, Popup as PopupInstance} from "maplibre-gl";
import {MapMarkerInstance} from "./MapMarker";
import {Popup, PopupProps, PopupEvent} from "react-map-gl/maplibre";
import {getManager} from "../../../ts/manager/Manager";
import BasicComponent from "../../../tsx/component/BasicComponent";
import {requirePoint} from "../utils/MapUtils";

export interface IMapPopupProps
{
    popup?:PopupProps & PopupOptions
    contentVersion?:number
    closeOnMapClick?:boolean;
    closeOnEscape?:boolean;
    preventDefaultScroll?:boolean;
    focusOffset?:PointLike;
}

interface IProps extends IMapPopupProps
{
    map:MapLibreMapInstance
}

export class MapPopupInstance extends BasicComponent<IProps>
{

    props: IProps;

    map:MapLibreMapInstance
    anchor: PositionAnchor = "bottom";
    offset:Offset = new Point(0, 0);
    popupContent: React.ReactNode = null!;
    popupRef = React.createRef<PopupInstance>();
    previousPopupMarker:MapMarkerInstance|null = null;
    popupProps:PopupProps & PopupOptions = null!;
    focusOffset:Point|null = null;
    selectedMarkerID: string|null = null;
    selectedObjectID: string|null = null;

    padding = 10;

    constructor(props: IProps)
    {
        super(props);
        this.props = props;
        this.map = this.props.map;
    }

    bindFunctions()
    {
        super.bindFunctions();
        this.handleOnOpen = this.handleOnOpen.bind(this);
        this.handleOnClose = this.handleOnClose.bind(this);
    }

    getActivePopupMarker( allowHidden:boolean = false):MapMarkerInstance|null
    {
        return !!this.map.selectedMarker && ((!!this.map.selectedMarker.popupContent && this.map.showSelectedMarkerPopup) || allowHidden) ? this.map.selectedMarker : null;
    }

    applyMaxPopupSize()
    {
        const popupMapMarker = this.getActivePopupMarker()
        if(!popupMapMarker){
            return;
        }
        const mapOuterContainer = this.map.getMapOuterContainer();
        const iconSize = popupMapMarker.getIcon()?.size ?? null;
        const iconSizeWidth = iconSize ? iconSize[0] : 0;
        const iconSizeHeight = iconSize ? iconSize[1] : 0;

        const verticalAnchor = this.verticalAnchor();
        const canSetWidth = (verticalAnchor !== "center") || !!popupMapMarker.focusOffset;
        const canSetHeight = !!popupMapMarker.focusOffset;

        let maxWidth = 0;
        let maxHeight = 0;
        if(canSetWidth){
            maxWidth = mapOuterContainer?.clientWidth ?? 0;
            const horizontalOffset = popupMapMarker.focusOffset?.x ?? 0
            maxWidth -= Math.abs(horizontalOffset * maxWidth);
            maxWidth -= iconSizeWidth;
            maxWidth = Math.floor(maxWidth)
        }

        if(canSetHeight){
            maxHeight =  mapOuterContainer?.clientHeight ?? 0;
            const verticalOffset = popupMapMarker.focusOffset?.y ?? 0
            maxHeight = ((verticalOffset + 1) / 2) * maxHeight;
            maxHeight -= iconSizeHeight;
            maxHeight = Math.floor(maxHeight)
        }

        if(maxWidth > 0){
            document.body.style.setProperty("--max-map-popover-width", maxWidth + "px");
        } else {
            document.body.style.removeProperty('--max-map-popover-width');
        }
        if(maxHeight > 0){
            document.body.style.setProperty("--max-map-popover-height", maxHeight + "px");
        } else {
            document.body.style.removeProperty('--max-map-popover-height');
        }
    }

    determineFocusOffset()
    {
        this.focusOffset = null;

        const fromPopUpOffset = this.props.focusOffset ?? null
        if(fromPopUpOffset){
            this.focusOffset = requirePoint(fromPopUpOffset);
            return;
        }

        const padding = this.padding;

        const popupMapMarker = this.getActivePopupMarker()
        const element = this.getPopup()?.getElement() ?? null;
        const outerContainer = this.map.getMapOuterContainer();

        const horizontalAnchor = this.horizontalAnchor();
        const verticalAnchor = this.verticalAnchor();
        const popUpOffset = popupMapMarker?.getIcon()?.getPopUpOffset(this.directionAnchor());

        if(!element || !popupMapMarker || !outerContainer || !popUpOffset){
            return;
        }

        const containerWidth =  outerContainer.clientWidth;
        const containerHeight =  outerContainer.clientHeight;

        const occupiedPopupWidth = element.offsetWidth;
        const occupiedPopupHeight = element.offsetHeight;

        const popupOffsetX = popUpOffset.x;
        const occupiedPopupWidthProp = occupiedPopupWidth / containerWidth;
        const paddingXProp = padding / containerWidth;
        const popupOffsetXProp = popupOffsetX / containerWidth;

        let xMinimum;
        let xMaximum;
        if(horizontalAnchor === "left"){
            xMinimum = -0.5 + paddingXProp;
            xMaximum = (-0.5 + occupiedPopupWidthProp + paddingXProp + popupOffsetXProp) * -1;
        } else if(horizontalAnchor === "center"){
            xMinimum = -0.5 + occupiedPopupWidthProp / 2 + paddingXProp - popupOffsetXProp;
            xMaximum = (-0.5 + occupiedPopupWidthProp / 2 + paddingXProp + popupOffsetXProp) * -1;
        } else {
            xMinimum = -0.5 + occupiedPopupWidthProp + paddingXProp - popupOffsetXProp;
            xMaximum = 0;
        }
        let xOffset = Math.min(0, xMaximum);
        xOffset = Math.max(xOffset, xMinimum);

        const popupOffsetY = popUpOffset.y;
        const occupiedPopupHeightProp = occupiedPopupHeight / containerHeight;
        const paddingYProp = padding / containerHeight;
        const popupOffsetYProp = popupOffsetY / containerHeight;

        let yMinimum;
        let yMaximum;
        if(verticalAnchor === "top"){
            yMinimum = -0.5 + paddingYProp;
            yMaximum = (-0.5 + occupiedPopupHeightProp + paddingYProp + popupOffsetYProp) * -1;
        } else if(verticalAnchor === "center"){
            yMinimum = -0.5 + occupiedPopupHeightProp / 2 + paddingYProp - popupOffsetYProp;
            yMaximum = (-0.5 + occupiedPopupHeightProp / 2 + paddingYProp + popupOffsetYProp) * -1;
        } else {
            yMinimum = -0.5 + occupiedPopupHeightProp + paddingYProp - popupOffsetYProp;
            yMaximum = 0;
        }
        let yOffset = Math.min(0, yMaximum);
        yOffset = Math.max(yOffset, yMinimum);
        if(!xOffset && !yOffset){
            return;
        }
        this.focusOffset = new Point(xOffset, yOffset)

    }

    willPopupFit(targetLngLat:LngLat): boolean
    {

        const popupMapMarker = this.getActivePopupMarker()

        const element = this.getPopup()?.getElement() ?? null;
        const outerContainer = this.map.getMapOuterContainer();
        const bounds = this.map.getMap()?.getBounds() ?? null;
        const popUpOffset = popupMapMarker?.getIcon()?.getPopUpOffset(this.directionAnchor()) ?? null;
        if(!element || !popupMapMarker || !outerContainer || !bounds || !popUpOffset){
            return false;
        }

        const locationManager = getManager().location;

        const distanceInMetresToBoundsWest = locationManager.getDistanceInMetresBetween(targetLngLat, new LngLat(bounds.getWest(), targetLngLat.lat));
        const distanceInMetresToBoundsEast = locationManager.getDistanceInMetresBetween(targetLngLat, new LngLat(bounds.getEast(), targetLngLat.lat));
        const distanceInMetresToBoundsNorth = locationManager.getDistanceInMetresBetween(targetLngLat, new LngLat(targetLngLat.lng, bounds.getNorth()));
        const distanceInMetresToBoundsSouth = locationManager.getDistanceInMetresBetween(targetLngLat, new LngLat(targetLngLat.lng, bounds.getSouth()));
        const boundsWidthInMetres = locationManager.getDistanceInMetresBetween(new LngLat(bounds.getWest(), bounds.getCenter().lat), new LngLat(bounds.getEast(), bounds.getCenter().lat));
        const boundsHeightInMetres = locationManager.getDistanceInMetresBetween(new LngLat(bounds.getCenter().lng, bounds.getNorth()), new LngLat(bounds.getCenter().lng, bounds.getSouth()));

        const containerWidth =  outerContainer.clientWidth;
        const containerHeight =  outerContainer.clientHeight;

        let distanceInPxToLeft = (distanceInMetresToBoundsWest / boundsWidthInMetres) * containerWidth;
        let distanceInPxToRight = (distanceInMetresToBoundsEast / boundsWidthInMetres) * containerWidth;
        let distanceInPxToTop = (distanceInMetresToBoundsNorth / boundsHeightInMetres) * containerHeight;
        let distanceInPxToBottom = (distanceInMetresToBoundsSouth / boundsHeightInMetres) * containerHeight;

        const padding = this.padding;

        const occupiedPopupWidth = element.offsetWidth;
        const occupiedPopupHeight = element.offsetHeight;

        const horizontalAnchor = this.horizontalAnchor();
        const verticalAnchor = this.verticalAnchor();
        const horizontalPopupOffset = (horizontalAnchor !== "center") && (verticalAnchor === "center") ? Math.abs(popUpOffset.x) : 0;
        const verticalPopUpOffset = verticalAnchor !== "center" ? Math.abs(popUpOffset.y) : 0;
        const totalOccupiedWidth = occupiedPopupWidth + horizontalPopupOffset + padding;
        const totalOccupiedHeight = occupiedPopupHeight + verticalPopUpOffset + padding;

        if(horizontalAnchor === "left"){
            distanceInPxToRight -= totalOccupiedWidth;
        } else if(horizontalAnchor === "center"){
            distanceInPxToRight -= totalOccupiedWidth / 2;
            distanceInPxToLeft -= totalOccupiedWidth / 2;
        } else {
            distanceInPxToLeft -= totalOccupiedWidth;
        }

        if(verticalAnchor === "top"){
            distanceInPxToBottom -= totalOccupiedHeight;
        } else if(verticalAnchor === "center"){
            distanceInPxToBottom -= totalOccupiedHeight / 2;
            distanceInPxToTop -= totalOccupiedHeight / 2;
        } else {
            distanceInPxToTop -= totalOccupiedHeight;
        }

        return (distanceInPxToLeft >= 0) && (distanceInPxToRight >= 0) && (distanceInPxToTop >= 0) && (distanceInPxToBottom >= 0);
    }

    verticalAnchor(): "bottom"|"top"|"center"
    {
        const bottomAnchors:PositionAnchor[] = ["bottom", "bottom-right", "bottom-left"];
        if(bottomAnchors.includes(this.anchor)){
            return "bottom";
        }
        const topAnchors:PositionAnchor[] = ["top", "top-right", "top-left"];
        if(topAnchors.includes(this.anchor)){
            return "top";
        }
        return "center";
    }

    horizontalAnchor(): "right"|"left"|"center"
    {
        const rightAnchors:PositionAnchor[] = ["right", "bottom-right", "top-right"];
        if(rightAnchors.includes(this.anchor)){
            return "right";
        }
        const leftAnchors:PositionAnchor[] = ["left", "bottom-left", "top-left"];
        if(leftAnchors.includes(this.anchor)){
            return "left";
        }
        return "center";
    }

    directionAnchor(): "bottom"|"top"|"right"|"left"|"center"
    {
        const verticalAnchor = this.verticalAnchor();
        return verticalAnchor !== "center" ? verticalAnchor : this.horizontalAnchor();
    }

    applyNextProps(nextProps:IProps, props?:IProps)
    {
        super.applyNextProps(nextProps, props);

        this.anchor = nextProps.popup?.anchor ?? this.anchor;
        let offset:Offset|null = null;
        let popupContent: React.ReactNode|null = null;
        let popupProps:PopupProps & PopupOptions|null = null;


        const popupMapMarker = this.getActivePopupMarker();
        let selectedMarkerID: string|null = null;
        let selectedObjectID: string|null = null;
        const markerLangLat = popupMapMarker?.getLngLat() ?? new LngLat(0, 0);
        if(popupMapMarker){

            selectedMarkerID = popupMapMarker.markerID;
            selectedObjectID = popupMapMarker.getSelectedObjectID()

            if(props){
                popupProps = props.popup === nextProps.popup ? this.popupProps : null;
                if((props.popup === nextProps.popup) && (popupMapMarker === this.previousPopupMarker)){
                    offset = this.offset;
                }
                if((popupMapMarker.popupContent === (this.previousPopupMarker?.popupContent ?? null)) && (selectedMarkerID === this.selectedMarkerID) && (selectedObjectID === this.selectedObjectID) && (nextProps.contentVersion === this.props.contentVersion)){
                    popupContent = this.popupContent;
                }
            }

            if(!offset){
                offset = nextProps.popup?.offset ?? (popupMapMarker.getIcon()?.getPopUpOffset(this.directionAnchor()) ?? null);
            }

            if(!popupContent){
                popupContent = <div className="map-marker-popup-content">
                    {popupMapMarker.popupContent ? (typeof popupMapMarker.popupContent === 'function' ? popupMapMarker.popupContent(popupMapMarker) : popupMapMarker.popupContent) : ""}
                </div>;
            }
        }

        if(!popupProps)
        {
            popupProps = {
                ...nextProps.popup,
                latitude: markerLangLat.lat,
                longitude: markerLangLat.lng,
                closeOnClick: nextProps.popup?.closeOnClick ?? false,
                maxWidth: nextProps.popup?.maxWidth ?? "unset",
                style: {
                    ...nextProps.popup?.style,
                    zIndex: nextProps.popup?.style?.zIndex ?? 7
                }
            }
        }
        this.selectedMarkerID = selectedMarkerID;
        this.selectedObjectID = selectedObjectID;
        this.previousPopupMarker = popupMapMarker;
        this.offset = offset ?? this.offset;
        this.popupContent = popupContent ?? <></>;
        this.popupProps = popupProps;
    }

    handleOnOpen(event: PopupEvent){
        if(!this.map.mounted){
            return;
        }
        this.removeFocus();

        this.applyMaxPopupSize();
        this.determineFocusOffset();
        if(this.props.popup?.onOpen){
            this.props.popup.onOpen(event);
        }
        this.refreshState()
        setTimeout(() => {
            if(this.props.preventDefaultScroll){
                window.scrollBy(0, 0);
            }
            this.map.scrollToView();
        }, 1)
    }


    componentDidMount()
    {
        super.componentDidMount();
        this.removeFocus();
    }

    removeFocus(){
        this.whenNextStateEstablished().then(() => {
            getManager().ui.removeFocus()
        })
    }

    handleOnClose(event: PopupEvent){
        if(!this.map.mounted){
            return;
        }
        this.getActivePopupMarker()?.onPopupClose();
        this.map.showSelectedMarkerPopup = false;
        if(this.props.popup?.onClose){
            this.props.popup.onClose(event);
        }
        this.map.refreshState()
        this.refreshState()
    }

    closePopup(): void
    {
        const popup = this.getPopup();
        if(!popup || !popup.isOpen()){
            return;
        }
        popup.remove();
    }

    openPopup(): void
    {
        const map = this.map;
        if(!map.mounted || !map.isMapReady){
            return;
        }

        if(this.getActivePopupMarker() !== this.previousPopupMarker){
            this.refreshState();
            return;
        }
        const popup = this.getPopup();
        const mapMapMap = map.getMap()?.getMap();
        if(!mapMapMap || !popup || popup.isOpen()){
            return;
        }
        popup.addTo(mapMapMap)
        this.refreshState();
    }

    getPopup(): PopupInstance|null
    {
        return this.popupRef.current;
    }

    renderComponent()
    {
        const popupMapMarker = this.getActivePopupMarker();
        if(!popupMapMarker || !this.map.showSelectedMarkerPopup){
            return <></>;
        }

        return <Popup
            {...this.popupProps}
            ref={this.popupRef}
            anchor={this.anchor}
            offset={this.offset}
            onOpen={this.handleOnOpen}
            onClose={this.handleOnClose}
            children={this.popupContent}
        />
    }
}
const MapPopup  = React.memo(MapPopupInstance);
export default MapPopup;